import {Component, inject, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {switchMap} from 'rxjs';
import {DatePipe, DecimalPipe} from '@angular/common';
import {IconComponent, ButtonComponent} from '@kin/spark-ui';
import {StepperComponent} from '../stepper/stepper.component';
import {PropertySidebarComponent} from '../property-sidebar/property-sidebar.component';
import {AuthService} from '../auth/auth.service';
import {AddressService} from '../property/address.service';
import {PropertyAssessmentService} from '../property/property-assessment.service';
import {UserProfileService} from '../user-profile.service';
import {CaseService} from '../case.service';

import {COUNTY_COPY, County, daysUntil, FILING_DEADLINE_DATE_FORMAT, getFilingDeadline, parseIsoDate} from '../county/county-config';

interface DetailRow {
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
}

@Component({
  selector: 'app-confirm',
  standalone: true,
  imports: [DatePipe, DecimalPipe, IconComponent, ButtonComponent, StepperComponent, PropertySidebarComponent],
  templateUrl: './confirm.component.html',
  styleUrl: './confirm.component.css',
})
export class ConfirmComponent implements OnInit {
  private router = inject(Router);
  private auth = inject(AuthService);
  private caseService = inject(CaseService);
  protected addressService = inject(AddressService);
  protected assessment = inject(PropertyAssessmentService);
  protected userProfile = inject(UserProfileService);

  private get county(): County {
    return this.assessment.data?.property?.countyCode ?? 'harris';
  }

  dateFormat = FILING_DEADLINE_DATE_FORMAT;

  /** Real per-case value once a case exists; the county's fallback placeholder until then. */
  get filingDeadline(): Date {
    const real = this.caseService.caseData?.filingDeadline;
    return real ? parseIsoDate(real) : getFilingDeadline(this.county);
  }

  get daysLeft(): number {
    return daysUntil(this.filingDeadline);
  }

  get pageTitle(): string {
    return COUNTY_COPY[this.county].pageTitle;
  }

  get filingNoun(): string {
    return COUNTY_COPY[this.county].filingNoun;
  }

  submitting = false;
  submitError = '';
  private hasCase = false;

  ngOnInit(): void {
    this.caseService.getCase().subscribe({
      next: (c) => { this.hasCase = !!c; },
    });
  }

  submit() {
    this.submitting = true;
    this.submitError = '';

    if (this.hasCase) {
      this.auth.updateProgress('sign').subscribe({
        next: () => this.router.navigate(['/sign']),
        error: () => this.router.navigate(['/sign']),
      });
      return;
    }

    this.caseService.createCase({ conditions: [] }).pipe(
      switchMap(() => this.auth.updateProgress('sign')),
    ).subscribe({
      next: () => this.router.navigate(['/sign']),
      error: () => {
        this.submitting = false;
        this.submitError = 'Something went wrong. Please try again.';
      },
    });
  }

  get details(): DetailRow[] {
    const addr = this.addressService;
    const profile = this.userProfile.profile;
    return [
      {label: 'Property owner', value: profile?.owner.name ?? ''},
      {
        label: 'Property',
        value: `${addr.displayStreet}, ${addr.displayCityStateZip}`,
        sub: `Parcel ID ${profile?.property?.parcelId ?? ''} · ${profile?.property?.county ?? ''}`,
      },
      {label: 'Email', value: profile?.owner.email ?? ''},
      // Update this to point to this year.
      {label: 'Tax year', value: '2026'},
    ];
  }

}
